import React from 'react';

import { FormattedMessage, FormattedNumber, IntlShape } from 'react-intl';

import { ReactTableEnums, ReactTableUtils } from '@nhl/nhl-breakaway-ui';
import { CellContext, createColumnHelper, Row } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { createGenericSortableStatsCol, StatsColumnType } from '~/core/commonTableColumns';
import { Alignments, PlayerPositionLong, PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import { getSize } from '~/core/tableColumns/utils';
import { LocalizedAttribute } from '~/core/types';
import {
    FormatPlayerPosition,
    getPlayerHref,
    getSecondsFromMinutesSecondsLabel,
    parseValueFromLocalizedAttribute,
} from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { Goalie, GoalieSeasonStat, Skater, SkaterSeasonStat } from '../../types';

import { RosterLink, SIZING } from './styles';

const { statsDescriptions, playerInfoDescriptions } = CommonMessages;

type RosterPlayer = Goalie | GoalieSeasonStat | Skater | SkaterSeasonStat;

const columnHelper = createColumnHelper<RosterPlayer>();
export const createSortablePlayerCol = (intl: IntlShape, columnDef: StatsColumnType) => {
    columnDef.size = columnDef.size || 48;
    return createGenericSortableStatsCol(columnHelper)(intl, columnDef);
};

export const createColumn = (
    intl: IntlShape,
    statAbbrev: PlayerStatAbbrev,
    statName: PlayerStatNames,
    options?: {
        acc?: string;
        isHidden?: boolean;
        size?: number;
    }
) =>
    createGenericSortableStatsCol(columnHelper)(intl, {
        acc: options?.acc || statName,
        header: statsDescriptions.abbreviations[statAbbrev],
        meta: {
            ...addSortableHeaderTitle(statsDescriptions.long[statName], intl),
            hidden: options?.isHidden,
        },
        size: options?.size || 48,
    });

export const getTimeFromTimeOnIceLabel = (object: Goalie | Skater | SkaterSeasonStat): number | undefined =>
    object.toi ? getSecondsFromMinutesSecondsLabel(object.toi) : undefined;

export const getTimeFromAvgTimeOnIceLabel = (object: SkaterSeasonStat): number | undefined =>
    object.avgTimeOnIce ? getSecondsFromMinutesSecondsLabel(object.avgTimeOnIce) : undefined;

export const getTimeFromMinutesSecondsLabel = (object: GoalieSeasonStat): number | undefined =>
    object?.toi ? getSecondsFromMinutesSecondsLabel(object.toi) : undefined;

export const SWEATER_NUM_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'sweaterNumber',
        header: playerInfoDescriptions.abbreviations.number,
        size: SIZING.sweaterNumber.small,
        meta: {
            ...addSortableHeaderTitle(playerInfoDescriptions.abbreviations.number, intl),
            className: 'sticky sweater-number',
            headerClassName: 'sticky sweater-number',
        },
    }),
    maxSize: getSize(intl, playerInfoDescriptions.abbreviations.number, SIZING.sweaterNumber.small),
});

export const NAME_COL = (pos: PlayerPositionLong, isWebview: boolean, intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'name',
        header: playerInfoDescriptions.long[pos],
        size: SIZING.playerName.small,
        alignHeader: Alignments.left,
        meta: {
            ...addSortableHeaderTitle(playerInfoDescriptions.long[pos], intl),
            headerClassName: 'sticky player-name left-aligned',
            className: 'sticky player-name left-aligned',
            scope: ReactTableEnums.Scopes.ROW,
        },
    }),
    cell: ({ row }: CellContext<RosterPlayer, LocalizedAttribute>) => (
        <RosterLink href={getPlayerHref(row.original.playerId, isWebview)} target="_blank" rel="noreferrer">
            {parseValueFromLocalizedAttribute(row.original.name)}
        </RosterLink>
    ),
    maxSize: getSize(intl, playerInfoDescriptions.long[pos], SIZING.playerName.small),
    sortingFn: (a: Row<SkaterSeasonStat | GoalieSeasonStat>, b: Row<SkaterSeasonStat | GoalieSeasonStat>) => {
        // this is necessary because the names are in the format 'J. Hughes'
        const reversedA = a.original.name.default.split(' ').reverse().join(' ');
        const reversedB = b.original.name.default.split(' ').reverse().join(' ');
        return reversedB.localeCompare(reversedA);
    },
});

export const POSITION_COL = (intl: IntlShape, pos: PlayerPositionLong) => ({
    ...createColumn(intl, PlayerStatAbbrev.position, PlayerStatNames.position, {
        isHidden: pos === PlayerPositionLong.defensemen,
    }),
    cell: (cell) => <FormatPlayerPosition position={cell?.getValue()} />,
});

export const GAMES_PLAYED_COL = (intl: IntlShape) =>
    createColumn(intl, PlayerStatAbbrev.gamesPlayed, PlayerStatNames.gamesPlayed);

const PENALTY_MINUTES_COL = (intl: IntlShape) =>
    createColumn(intl, PlayerStatAbbrev.penaltyMinutes, PlayerStatNames.penaltyMinutes, { acc: 'pim' });

export const TOI_COL = (intl: IntlShape) => ({
    ...createColumn(intl, PlayerStatAbbrev.timeOnIce, PlayerStatNames.timeOnIce, { acc: 'toi' }),
    sortDescFirst: true,
    sortingFn: ReactTableUtils.sortUndefinedAtBottom(getTimeFromTimeOnIceLabel),
});

const FO_PCTG_COL = (intl: IntlShape, pos: PlayerPositionLong) => ({
    ...createColumn(intl, PlayerStatAbbrev.faceoffPctg, PlayerStatNames.faceoffWinningPctg, {
        isHidden: pos === PlayerPositionLong.defensemen,
    }),
    cell: (cell: CellContext<RosterPlayer, number>) => {
        const value = cell.getValue();
        if (value === undefined) {
            return <FormattedMessage {...CommonMessages.statsDescriptions.noStatIndicator} />;
        }
        return <FormattedNumber value={value * 100} minimumFractionDigits={1} maximumFractionDigits={1} />;
    },
});

const PLUS_MINUS_COL = (intl: IntlShape) => ({
    ...createColumn(intl, PlayerStatAbbrev.plusMinus, PlayerStatNames.plusMinus),
    cell: (cell: CellContext<RosterPlayer, number>) =>
        cell.getValue() !== undefined ? (
            <FormattedNumber value={cell.getValue()} signDisplay="exceptZero" />
        ) : (
            <FormattedMessage {...CommonMessages.statsDescriptions.noStatIndicator} />
        ),
});

// Column Groupings
const PREGAME_COLS = (pos: PlayerPositionLong, isWebview: boolean, intl: IntlShape, isLimitedScoring: boolean) => {
    const hiddenOptions = { isHidden: isLimitedScoring };
    return [
        SWEATER_NUM_COL(intl),
        NAME_COL(pos, isWebview, intl),
        POSITION_COL(intl, pos),
        GAMES_PLAYED_COL(intl),
        createColumn(intl, PlayerStatAbbrev.goals, PlayerStatNames.goals),
        createColumn(intl, PlayerStatAbbrev.assists, PlayerStatNames.assists),
        createColumn(intl, PlayerStatAbbrev.points, PlayerStatNames.points),
        PLUS_MINUS_COL(intl),
        PENALTY_MINUTES_COL(intl),
        createColumn(intl, PlayerStatAbbrev.powerPlayGoals, PlayerStatNames.powerPlayGoals),
        createColumn(intl, PlayerStatAbbrev.gameWinningGoals, PlayerStatNames.gameWinningGoals, hiddenOptions),
        createColumn(intl, PlayerStatAbbrev.shots, PlayerStatNames.shotsOnGoal, { acc: 'shots' }),
        createColumn(intl, PlayerStatAbbrev.blocks, PlayerStatNames.blockedShots, hiddenOptions),
        createColumn(intl, PlayerStatAbbrev.hits, PlayerStatNames.hits, hiddenOptions),
        {
            ...createColumn(intl, PlayerStatAbbrev.avgTimeOnIce, PlayerStatNames.avgTimeOnIce, hiddenOptions),
            sortDescFirst: true,
            sortingFn: ReactTableUtils.sortUndefinedAtBottom(getTimeFromAvgTimeOnIceLabel),
        },
        FO_PCTG_COL(intl, pos),
    ];
};

const LIVE_POST_GAME_COLS = (
    pos: PlayerPositionLong,
    isWebview: boolean,
    intl: IntlShape,
    isLimitedScoring: boolean
) => {
    const hiddenOptions = { isHidden: isLimitedScoring };
    return [
        SWEATER_NUM_COL(intl),
        NAME_COL(pos, isWebview, intl),
        POSITION_COL(intl, pos),
        createColumn(intl, PlayerStatAbbrev.goals, PlayerStatNames.goals),
        createColumn(intl, PlayerStatAbbrev.assists, PlayerStatNames.assists),
        createColumn(intl, PlayerStatAbbrev.points, PlayerStatNames.points),
        PLUS_MINUS_COL(intl),
        PENALTY_MINUTES_COL(intl),
        {
            ...createColumn(intl, PlayerStatAbbrev.timeOnIce, PlayerStatNames.timeOnIce, {
                acc: 'toi',
                isHidden: isLimitedScoring,
            }),
            sortDescFirst: true,
            sortingFn: ReactTableUtils.sortUndefinedAtBottom(getTimeFromTimeOnIceLabel),
        },
        createColumn(intl, PlayerStatAbbrev.shifts, PlayerStatNames.shifts, {
            isHidden: isLimitedScoring,
            size: 60,
        }),
        createColumn(intl, PlayerStatAbbrev.powerPlayGoals, PlayerStatNames.powerPlayGoals),
        createColumn(intl, PlayerStatAbbrev.shots, PlayerStatNames.shotsOnGoal, { acc: 'sog' }),
        createColumn(intl, PlayerStatAbbrev.blocks, PlayerStatNames.blockedShots, hiddenOptions),
        createColumn(intl, PlayerStatAbbrev.hits, PlayerStatNames.hits, hiddenOptions),
        createColumn(intl, PlayerStatAbbrev.giveaways, PlayerStatNames.giveaways, hiddenOptions),
        createColumn(intl, PlayerStatAbbrev.takeaways, PlayerStatNames.takeaways, hiddenOptions),
        FO_PCTG_COL(intl, pos),
    ];
};

export const skaterColumns = (
    pos: PlayerPositionLong,
    isPregame: boolean,
    isLimitedScoring: boolean,
    isWebview: boolean,
    intl: IntlShape
) =>
    isPregame
        ? PREGAME_COLS(pos, isWebview, intl, isLimitedScoring)
        : LIVE_POST_GAME_COLS(pos, isWebview, intl, isLimitedScoring);
